import React, { useRef, useState, useCallback, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { dateOnlyBodyTemplate } from '../../../../../utils/dateUtils';
import { useLanguage } from '../../../../../contexts/LanguageContext';

const WarrantyPeriodTable = ({ data, loading, onPage, lazyParams, totalCount, onEdit }) => {
    const { th } = useLanguage();
    const dataTableRef = useRef(null);
    const columnsRef = useRef(null);

    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(0);

    const normalizedData = useMemo(() => {
        if (Array.isArray(data)) return data;
        if (Array.isArray(data?.items)) return data.items;
        return [];
    }, [data]);

    const sortedData = useMemo(() => {
        if (!sortField || sortOrder === 0) return normalizedData;

        return [...normalizedData].sort((a, b) => {
            let aValue = a[sortField];
            let bValue = b[sortField];

            if (aValue == null && bValue == null) return 0;
            if (aValue == null) return sortOrder;
            if (bValue == null) return -sortOrder;

            if (typeof aValue === 'string' && typeof bValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
            }

            if (sortField.toLowerCase().includes('date') || sortField === 'sellDate') {
                aValue = new Date(aValue);
                bValue = new Date(bValue);
            }

            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return sortOrder * (aValue - bValue);
            }

            if (aValue < bValue) return -sortOrder;
            if (aValue > bValue) return sortOrder;
            return 0;
        });
    }, [normalizedData, sortField, sortOrder]);

    const handleSort = (e) => {
        setSortField(e.sortField);
        setSortOrder(e.sortOrder);
    };

    const getColumnsCallback = useCallback((el) => {
        if (el && el.getColumns && !columnsRef.current) {
            columnsRef.current = el.getColumns();
        }
        dataTableRef.current = el;
    }, []);

    const actionBodyTemplate = (rowData) => (
        <div className="flex gap-2 justify-center">
            <i className="pi pi-pencil cursor-pointer text-blue-500 hover:text-blue-700" style={{ fontSize: '1.1rem' }} onClick={() => onEdit(rowData)} />
            <i className="pi pi-history cursor-pointer text-blue-500 hover:text-blue-700" style={{ fontSize: '1.1rem' }} title={th('warrantyClaims.tooltips.history')}></i>
        </div>
    );

    return (
        <DataTable
            className="p-datatable-gridlines"
            value={sortedData}
            paginator
            rows={lazyParams.rows || 10}
            loading={loading}
            globalFilterFields={['productGroup', 'carItemCode', 'startDate', 'endDate']}
            totalRecords={totalCount}
            showGridlines
            lazy
            first={lazyParams.first}
            onPage={onPage}
            onSort={handleSort}
            sortField={sortField}
            sortOrder={sortOrder}
            dataKey="id"
            filterDisplay="menu"
            emptyMessage={th('vehicleWarrantyPeriod.emptyMessage')}
            rowsPerPageOptions={[5, 10, 25, 50, 100, 500, 1000]}
            tableStyle={{ minWidth: '60rem' }}
            ref={getColumnsCallback}
            sortMode="single"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        >
            <Column body={actionBodyTemplate} style={{ width: '5%' }} align="center" />
            <Column sortable field="startDate" header={th('vehicleWarrantyPeriod.columnHeaders.startDate')} body={(rowData) => dateOnlyBodyTemplate(rowData, 'startDate')} />
            <Column sortable field="endDate" header={th('vehicleWarrantyPeriod.columnHeaders.endDate')} body={(rowData) => dateOnlyBodyTemplate(rowData, 'endDate')} />
            <Column sortable field="productGroup" header={th('vehicleWarrantyPeriod.columnHeaders.productGroup')} />
            <Column sortable field="carItemCode" header={th('vehicleWarrantyPeriod.columnHeaders.carItemCode')} />
            <Column sortable field="modelYear" header={th('vehicleWarrantyPeriod.columnHeaders.modelYear')} />
            <Column sortable field="warrantyPeriod" header={th('vehicleWarrantyPeriod.columnHeaders.warrantyPeriod')} />
            <Column sortable field="warrantyKmInfo" header={th('vehicleWarrantyPeriod.columnHeaders.warrantyKmInfo')} />
        </DataTable>
    );
};

export default WarrantyPeriodTable;
